import type {
  ChatUiPlanProposal,
  ChatUiQuestionnaire,
} from "./chatUiActionHelpers";

const QUESTIONNAIRE_KEY = "fintheon.chat.pendingQuestionnaire.v1";
const QUESTIONNAIRE_TTL_MS = 24 * 60 * 60 * 1000;
const PLAN_PROPOSAL_KEY = "fintheon.chat.pendingPlanProposal.v1";
const PLAN_PROPOSAL_TTL_MS = 24 * 60 * 60 * 1000;

interface StoredQuestionnaire {
  requestId: string | null;
  questionnaire: ChatUiQuestionnaire;
  savedAt: number;
}

interface StoredPlanProposal {
  requestId: string | null;
  proposal: ChatUiPlanProposal;
  savedAt: number;
}

function readStoredQuestionnaire(): StoredQuestionnaire | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(QUESTIONNAIRE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredQuestionnaire;
    if (Date.now() - parsed.savedAt <= QUESTIONNAIRE_TTL_MS) return parsed;
    window.localStorage.removeItem(QUESTIONNAIRE_KEY);
    return null;
  } catch {
    return null;
  }
}

export function loadPendingQuestionnaire(): ChatUiQuestionnaire | null {
  return readStoredQuestionnaire()?.questionnaire ?? null;
}

export function savePendingQuestionnaire(
  requestId: string | null,
  questionnaire: ChatUiQuestionnaire,
) {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(
    QUESTIONNAIRE_KEY,
    JSON.stringify({ requestId, questionnaire, savedAt: Date.now() }),
  );
}

export function clearPendingQuestionnaire(actionId: string) {
  if (typeof window === "undefined") return;
  const stored = readStoredQuestionnaire();
  if (stored?.questionnaire.actionId === actionId) {
    window.localStorage.removeItem(QUESTIONNAIRE_KEY);
  }
}

function readStoredPlanProposal(): StoredPlanProposal | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(PLAN_PROPOSAL_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredPlanProposal;
    if (Date.now() - parsed.savedAt <= PLAN_PROPOSAL_TTL_MS) return parsed;
    window.localStorage.removeItem(PLAN_PROPOSAL_KEY);
    return null;
  } catch {
    return null;
  }
}

export function loadPendingPlanProposal(
  surfaceId?: string | null,
  workspaceId?: string | null,
): ChatUiPlanProposal | null {
  const proposal = readStoredPlanProposal()?.proposal ?? null;
  if (!proposal || proposal.status !== "pending") return null;
  if (surfaceId === "narrativeflow") {
    if (proposal.surface !== "narrativeflow") return null;
    if (workspaceId && proposal.workspaceId !== workspaceId) {
      window.localStorage.removeItem(PLAN_PROPOSAL_KEY);
      return null;
    }
    if (!workspaceId && proposal.workspaceId) return null;
    return proposal;
  }
  return proposal.surface === "narrativeflow" ? null : proposal;
}

export function savePendingPlanProposal(
  requestId: string | null,
  proposal: ChatUiPlanProposal,
) {
  if (typeof window === "undefined") return;
  if (proposal.status !== "pending") return;
  window.localStorage.setItem(
    PLAN_PROPOSAL_KEY,
    JSON.stringify({ requestId, proposal, savedAt: Date.now() }),
  );
}

export function clearPendingPlanProposal(actionId: string) {
  if (typeof window === "undefined") return;
  const stored = readStoredPlanProposal();
  if (stored?.proposal.actionId === actionId) {
    window.localStorage.removeItem(PLAN_PROPOSAL_KEY);
  }
}
